import type { BoundaryCoordinates, Coordinates } from '$lib/components/map/types';
import type {
	MainPoiCategory,
	PoiData,
	PoiFilterId,
	PoiFilterValue,
} from '$lib/components/pois/types';
import type { SelectedAddress, SelectedPoi } from '$lib/services/urls/searchParams';
import type { PageLoadEvent as MapPageLoadEvent } from './map/[[category=poiCategory]]/[...address]/$types';
import type { PageLoadEvent as MapDatasheetPageLoadEvent } from './map/[[category=poiCategory]]/poi/[...poi]/$types';
import type { PageLoadEvent as MapFiltersPageLoadEvent } from './map/filters/$types';
import type { PageLoadEvent as RouteItineraryPageLoadEvent } from './routes/$types';
import type { PageLoadEvent as RouteCorridorPageLoadEvent } from './routes/(details)/on-the-way/$types';
import type { PageLoadEvent as RouteCorridorFiltersPageLoadEvent } from './routes/(details)/on-the-way/filters/$types';
import type { PageLoadEvent as RouteCorridorPoiPageLoadEvent, PageLoadEvent as RouteOnTheWayPoiPageLoadEvent } from './routes/(details)/on-the-way/poi/$types';
import type { PageLoadEvent as RouteDetailsSearchPageLoadEvent } from './routes/(details)/search/$types';
import type { PageLoadEvent as RouteFiltersPageLoadEvent } from './routes/(details)/search/filters/$types';
import type { PageLoadEvent as RouteDatasheetPageLoadEvent } from './routes/(details)/search/poi/$types';
import type { PageLoadEvent as ZoiPageLoadEvent } from './routes/(details)/zoi/$types';
import type { PageLoadEvent as ZoiDatasheetPageLoadEvent } from './routes/(details)/zoi/poi/$types';
import type { PoisStoreContent } from './types';
import { browser } from '$app/environment';
import { getPOIStore, type PoiFiltersInput, searchPOIsFromAddressStore, searchPOIsStore } from '$houdini';
import { defaultMapBoundsByCountry } from '$lib/components/map/constants';
import { defaultPoiCategories } from '$lib/constants/searchParams';
import { AddressEntitiesByAddressLength } from '$lib/seo/address';
import { type AugmentedHoudiniStore, augmentHoudiniStore } from '$lib/utils/augmentedHoudiniStore';
import { error } from '@sveltejs/kit';
import { getCountryFromLocale } from '@viamichelin/business';
import { derived, get, readable, type Readable } from 'svelte/store';
import { paramsHasCategory } from '../../params/poiCategory';

const POIS_PER_PAGE_MULTI_POI = 30;
const POIS_PER_PAGE_MONO_POI = 50;

type PageLoadEvent =
	| RouteDetailsSearchPageLoadEvent
	| RouteItineraryPageLoadEvent
	| RouteFiltersPageLoadEvent
	| RouteDatasheetPageLoadEvent
	| MapPageLoadEvent
	| MapFiltersPageLoadEvent
	| MapDatasheetPageLoadEvent
	| ZoiDatasheetPageLoadEvent;

type AugmentedSearchPOIsStore = AugmentedHoudiniStore<searchPOIsStore>;
type AugmentedSearchPOIsFromAddressStore = AugmentedHoudiniStore<searchPOIsFromAddressStore>;

export function createSearchPoisStore() {
	// eslint-disable-next-line new-cap
	return augmentHoudiniStore(new searchPOIsStore());
}

export function createSearchPOIsFromAddressStore() {
	// eslint-disable-next-line new-cap
	return augmentHoudiniStore(new searchPOIsFromAddressStore());
}

export function getPoiFiltersFromSearchParams(
	filters?: Partial<Record<PoiFilterId, PoiFilterValue>>,
): PoiFiltersInput[] {
	return (Object.entries(filters ?? {}) as [PoiFilterId, PoiFilterValue][]).map(([key, value]) => ({
		id: key,
		value,
	}));
}

export function getPoiFilters(
	filters?: Partial<Record<PoiFilterId, PoiFilterValue>>,
): PoiFiltersInput[] {
	const filtersFromSearchParams = getPoiFiltersFromSearchParams(filters);
	return filtersFromSearchParams;
}

export function enrichPoisStoreWithFetchedPoiData(
	poisStore: Readable<Omit<PoisStoreContent, 'selectedPoiData'>>,
	poiData: PoiData,
): Readable<PoisStoreContent> {
	return derived(poisStore, ($pois) => {
		if ($pois.poisList.find(poi => poi.id === poiData.id)) {
			return { ...$pois, selectedPoiData: poiData };
		}
		return { ...$pois, poisList: [poiData, ...$pois.poisList], selectedPoiData: poiData };
	});
}

type EnrichPoiStorePageLoadEvent =
	| PageLoadEvent
	| RouteCorridorPoiPageLoadEvent
	| RouteCorridorFiltersPageLoadEvent
	| ZoiPageLoadEvent
	| RouteCorridorPageLoadEvent
	| RouteOnTheWayPoiPageLoadEvent;

export async function enrichPoisStoreWithSelectedPoiData(
	poisStore: Readable<Omit<PoisStoreContent, 'selectedPoiData'>>,
	selectedPoi: SelectedPoi | undefined,
	event: EnrichPoiStorePageLoadEvent,
): Promise<Readable<PoisStoreContent>> {
	if (!selectedPoi) {
		return derived(poisStore, ($store) => {
			return { ...$store, selectedPoiData: undefined };
		});
	}

	const selectedPoiData = get(poisStore).poisList.find(poi => poi.id === selectedPoi.id);
	if (selectedPoiData) {
		return derived(poisStore, ($store) => {
			return { ...$store, selectedPoiData };
		});
	}

	// eslint-disable-next-line new-cap
	const getPOI = new getPOIStore();
	await getPOI
		.fetch({
			event,
			blocking: true,
			variables: {
				input: { id: selectedPoi.id, type: selectedPoi.type },
			},
		})
		.catch((error) => {
			// Workaround to make sure we subscribe and unsubscribe after an error is thrown
			// This avoids a memory leak coming from the houdini store
			//
			// See https://github.com/HoudiniGraphql/houdini/issues/1294
			get(getPOI);

			throw error;
		});

	const poiData = get(getPOI).data?.getPOIFromId.poi;

	if (!poiData) {
		throw error(404);
	}

	return enrichPoisStoreWithFetchedPoiData(poisStore, poiData);
}

interface GetPoiDataFromAddressArgs {
	urlAddress: string;
	event: PageLoadEvent;
	page: number;
	categories: MainPoiCategory[];
	searchPOIFromAddressStore: AugmentedSearchPOIsFromAddressStore;
}

async function getPoiDataFromAddressStore({
	urlAddress,
	event,
	page,
	categories,
	searchPOIFromAddressStore,
}: GetPoiDataFromAddressArgs) {
	const addressEntities = urlAddress.split('/');
	const addressEntityType = AddressEntitiesByAddressLength.at(addressEntities.length - 1);
	const perPage = categories.length > 1 ? POIS_PER_PAGE_MULTI_POI : POIS_PER_PAGE_MONO_POI;

	await searchPOIFromAddressStore
		.fetch({
			blocking: !browser,
			event,
			variables: {
				perPage,
				page,
				types: categories,
				query: urlAddress.replaceAll('/', '-'),
				addressEntityType,
			},
		})
		.catch((error) => {
			// Workaround to make sure we subscribe and unsubscribe after an error is thrown
			// This avoids a memory leak coming from the houdini store
			//
			// See https://github.com/HoudiniGraphql/houdini/issues/1294
			get(searchPOIFromAddressStore);

			throw error;
		});

	return derived(searchPOIFromAddressStore, ($store) => {
		const baseData = {
			fetching: $store.fetching,
			hasError: !!$store.errors,
		};

		let data: PoisStoreContent = {
			...baseData,
			poisList: [],
			poisCount: 0,
			pageCount: 0,
			selectedAddressCenter: undefined,
			mapBounds: undefined,
			selectedAddress: undefined,
			poiCategories: categories,
			locationLocalizedHierarchies: undefined,
			nearbyCitiesHierarchies: undefined,
			cityHierarchy: undefined,
			metaInfo: undefined,
		};

		if ($store.data && $store.data.searchPOIsFromAddress) {
			const {
				poiList: { items, pageCount, totalCount },
				mapLocation,
				address: { formattedAddress, city, country, region, zipCode },
				locationLocalizedHierarchies,
				nearbyCitiesHierarchies,
				cityHierarchy,
				metaInfo,
			} = $store.data.searchPOIsFromAddress;

			const bounds = mapLocation.viewPort;
			const mapBounds = [
				[bounds.southwest.lng, bounds.southwest.lat],
				[bounds.northeast.lng, bounds.northeast.lat],
			] satisfies BoundaryCoordinates;

			data = {
				...baseData,
				poisList: items ?? [],
				poisCount: totalCount,
				fetching: $store.fetching,
				pageCount,
				selectedAddressCenter: mapLocation.location,
				mapBounds,
				selectedAddress: formattedAddress
					? {
							address: formattedAddress,
							city: city ?? undefined,
							coordinates: mapLocation.location,
							countryCode: country?.code,
							region: region ?? undefined,
							zipCode: zipCode ?? undefined,
							entityType: addressEntityType,
						}
					: undefined,
				poiCategories: categories,
				locationLocalizedHierarchies: locationLocalizedHierarchies ?? undefined,
				nearbyCitiesHierarchies: nearbyCitiesHierarchies ?? undefined,
				cityHierarchy: cityHierarchy ?? undefined,
				metaInfo: metaInfo ?? undefined,
			};
		}

		return data;
	});
}

interface GetPoiDataFromBoundsArgs {
	event: PageLoadEvent;
	page: number;
	categories: MainPoiCategory[];
	filters?: Partial<Record<PoiFilterId, PoiFilterValue>>;
	mapBounds: BoundaryCoordinates;
	selectedAddressCenter?: Coordinates;
	selectedAddress?: SelectedAddress;
	searchPOIStore: AugmentedSearchPOIsStore;
}

async function getPoiDataFromBoundsStore({
	event,
	page,
	categories,
	filters,
	mapBounds,
	selectedAddressCenter,
	selectedAddress,
	searchPOIStore,
}: GetPoiDataFromBoundsArgs) {
	const perPage = categories.length > 1 ? POIS_PER_PAGE_MULTI_POI : POIS_PER_PAGE_MONO_POI;
	const bounds = {
		southwest: { lng: mapBounds[0][0], lat: mapBounds[0][1] },
		northeast: { lng: mapBounds[1][0], lat: mapBounds[1][1] },
	};

	await searchPOIStore
		.fetch({
			blocking: !browser,
			event,
			variables: {
				input: {
					perPage,
					page,
					types: categories,
					bounds,
					// When we have a selected address with a entityType (city, country...) defined
					// and that bounds are synced with the selected address bounds, we give it
					// to the BFF so it can give us SEO data
					addressQuery:
						selectedAddress?.coordinates
						&& selectedAddress?.entityType
						&& selectedAddress?.boundsSync
							? {
								// Empty string because we search using the coordinates
									query: '',
									entityType: selectedAddress.entityType,
									coordinates: selectedAddress.coordinates,
								}
							: undefined,
					poiFilters: getPoiFilters(filters),
				},
			},
		})
		.catch((error) => {
			// Workaround to make sure we subscribe and unsubscribe after an error is thrown
			// This avoids a memory leak coming from the houdini store
			//
			// See https://github.com/HoudiniGraphql/houdini/issues/1294
			get(searchPOIStore);

			throw error;
		});

	return derived(searchPOIStore, ($store) => {
		const baseData = {
			fetching: $store.fetching,
			hasError: !!$store.errors,
		};

		let data: PoisStoreContent = {
			...baseData,
			poisList: [],
			poisCount: 0,
			pageCount: 0,
			selectedAddressCenter: undefined,
			mapBounds: undefined,
			selectedAddress: undefined,
			poiCategories: categories,
			locationLocalizedHierarchies: undefined,
			nearbyCitiesHierarchies: undefined,
			cityHierarchy: undefined,
			metaInfo: undefined,
		};

		if ($store.data && $store.data.getPOIs) {
			const { items, pageCount, totalCount, availabilityStatus } = $store.data.getPOIs.poiList;
			const { locationLocalizedHierarchies, nearbyCitiesHierarchies, cityHierarchy, metaInfo }
			= $store.data.getPOIs;

			data = {
				...baseData,
				availabilityStatus: availabilityStatus ?? undefined,
				poisList: items ?? [],
				poisCount: totalCount,
				pageCount,
				selectedAddressCenter,
				mapBounds,
				selectedAddress,
				poiCategories: categories,
				locationLocalizedHierarchies: locationLocalizedHierarchies ?? undefined,
				nearbyCitiesHierarchies: nearbyCitiesHierarchies ?? undefined,
				cityHierarchy: cityHierarchy ?? undefined,
				metaInfo: metaInfo ?? undefined,
			};
		}

		return data;
	});
}

interface GetPoiStoreArgs {
	urlAddress?: string;
	event: any;
	searchBounds?: BoundaryCoordinates;
	searchPOIStore: AugmentedSearchPOIsStore;
	searchPOIFromAddressStore: AugmentedSearchPOIsFromAddressStore;
	poiCategories?: MainPoiCategory[];
}

export async function getPoisStore({
	urlAddress,
	event,
	searchBounds,
	searchPOIStore,
	searchPOIFromAddressStore,
	poiCategories,
}: GetPoiStoreArgs): Promise<Readable<Omit<PoisStoreContent, 'selectedPoiData'>>> {
	const { locale, hasPartnerAdinmap, searchParams } = await event.parent();
	const { filters, page } = searchParams;
	poiCategories = poiCategories ?? searchParams.poiCategories;
	const poiCategory = paramsHasCategory(event.params) ? event.params.category : undefined;
	let categories = poiCategories ?? (poiCategory ? [poiCategory] : undefined) ?? defaultPoiCategories;

	if (!hasPartnerAdinmap) {
		categories = categories.filter(category => category !== 'PARTNER');
	}

	const selectedAddress = searchParams.selectedAddress;
	const selectedAddressCenter = searchParams.center ?? undefined;
	const mapBounds = searchParams.bounds ?? searchBounds ?? defaultMapBoundsByCountry[getCountryFromLocale(locale)];

	if (categories.length === 0) {
		return readable({
			poisList: [],
			poisCount: 0,
			pageCount: 0,
			fetching: false,
			selectedAddressCenter,
			poiCategories: [],
			hasError: false,
			selectedAddress,
			mapBounds,
		});
	}

	const computedPoisStore = await (urlAddress
		? getPoiDataFromAddressStore({
				urlAddress,
				event,
				page,
				categories,
				searchPOIFromAddressStore,
			})
		: getPoiDataFromBoundsStore({
				event,
				page,
				categories,
				filters,
				mapBounds,
				selectedAddressCenter,
				selectedAddress,
				searchPOIStore,
			}));

	return computedPoisStore;
}
